import { Suspense, lazy } from "react";
import { isAxiosError } from "axios";
import { useLandingPage } from "../hooks/useLandingPage";
import { dashboardUrl } from "../config/source";
import NotFound from "./NotFound";
import LandingHeader from "./landing/LandingHeader";
import HeroSection from "./landing/HeroSection";
import AboutSection from "./landing/AboutSection";
import PartnersSection from "./landing/PartnersSection";
import LandingFooter from "./landing/LandingFooter";

// Lazy load ImpactSection (contains heavy Leaflet map, below the fold)
const ImpactSection = lazy(() => import("./landing/ImpactSection"));

interface LandingPageProps {
  subdomain: string;
}

export default function LandingPage({ subdomain }: LandingPageProps) {
  const { data, error } = useLandingPage(subdomain);

  // An unknown subdomain used to leave a blank page; say so instead.
  if (error) {
    return isAxiosError(error) && error.response?.status === 404
      ? <NotFound subdomain={subdomain} />
      : <NotFound message="This page could not be loaded. Please try again in a moment." />;
  }
  if (!data) {
    return null;
  }
  const dashboard = dashboardUrl(data.dashboard_url);

  // Collect additional logos
  const additionalLogos = [
    data.additional_logo_1,
    data.additional_logo_2,
    data.additional_logo_3,
  ];

  return (
    <div className="h-screen overflow-y-auto bg-white">
      {/* Smooth scroll behavior */}
      <style>{`
        .h-screen.overflow-y-auto {
          scroll-behavior: smooth;
        }
      `}</style>

      {/* Header */}
      <LandingHeader
        countyName={data.county_name}
        countyLogo={data.header_county_logo}
        additionalLogos={additionalLogos}
        dashboardUrl={dashboard}
      />

      {/* Hero Section */}
      <HeroSection
        heading={data.hero_heading}
        subheading={data.hero_subheading}
        paragraph={data.hero_paragraph}
        images={data.hero_images}
      />

      {/* About Section */}
      <AboutSection
        heading={data.about_heading}
        paragraph={data.about_paragraph}
        features={data.about_features}
      />

      {/* Impact Section - Lazy loaded (contains heavy map component) */}
      <Suspense fallback={<div className="py-20 bg-white" />}>
        <ImpactSection
          heading={data.impact_heading}
          subheading={data.impact_subheading}
          coverageAreas={data.coverage_areas}
          countyName={data.county_name}
          metrics={data.metrics}
          subdomain={subdomain}
        />
      </Suspense>

      {/* Partners Section */}
      <PartnersSection
        countyName={data.county_name}
        countyLogo={data.partner_county_logo}
        countySubtitle={data.partner_county_subtitle}
        countyDescription={data.partner_county_description}
        ctaText={data.cta_text}
        dashboardUrl={dashboard}
      />

      {/* Footer */}
      <LandingFooter countyName={data.county_name} />
    </div>
  );
}
